import * as React from 'react';

import { enumSidecarSortOption } from '@rematter/frontend-lib-api-client';
import { Button, Col, ConsoleDialog, Select } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';
import { LucideCheck } from 'lucide-react';

import { cameraCreateAtom } from '../../../_graphql/mutationCameraCreate';
import { sidecarsListQueryAtom } from '../../../_graphql/querySidecarsList';
import { useTranslations } from '../../../i18n/useTranslations';

interface RtspCameraCreateDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const $sidecarsForPicker = sidecarsListQueryAtom(() => ({
  variables: { sorting: [enumSidecarSortOption.NameAsc], limit: 100 },
}));

export const RtspCameraCreateDialog = ({
  isOpen,
  onClose,
  onSuccess,
}: RtspCameraCreateDialogProps) => {
  const t = useTranslations();
  const toast = useToast();

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [name, setName] = React.useState('');
  const [streamUrl, setStreamUrl] = React.useState('');
  const [snapshotUrl, setSnapshotUrl] = React.useState('');
  const [sidecarId, setSidecarId] = React.useState<string | null>(null);

  const createCamera = useSetAtom(cameraCreateAtom({}));
  const { data: sidecarsData } = useAtomValue($sidecarsForPicker);
  const sidecars = sidecarsData?.sidecars ?? [];

  const sidecarOptions = React.useMemo(
    () =>
      sidecars.map((s) => ({
        key: s.id,
        label: s.name,
      })),
    [sidecars],
  );

  const canSubmit =
    name.trim() !== '' && streamUrl.trim() !== '' && sidecarId !== null;

  const resetForm = React.useCallback(() => {
    setName('');
    setStreamUrl('');
    setSnapshotUrl('');
    setSidecarId(null);
  }, []);

  React.useEffect(() => {
    if (isOpen) {
      resetForm();
    }
  }, [isOpen, resetForm]);

  const handleClose = React.useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = React.useCallback(async () => {
    if (!canSubmit || !sidecarId) return;

    try {
      setIsSubmitting(true);
      await createCamera({
        sidecarId,
        uniqueId: '',
        driverType: 'rtsp',
        name: name.trim(),
        streamProfile: 'sub',
        streamUrl: streamUrl.trim(),
        snapshotUrl: snapshotUrl.trim() || undefined,
      });
      toast.success(t.hardwareView.rtspCameraCreateDialog.messages.success);
      onSuccess?.();
      handleClose();
    } catch (err) {
      toast.error(
        t.hardwareView.rtspCameraCreateDialog.messages.error(
          err instanceof Error ? err.message : 'Unknown error',
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    sidecarId,
    createCamera,
    name,
    streamUrl,
    snapshotUrl,
    toast,
    onSuccess,
    handleClose,
    t,
  ]);

  return (
    <ConsoleDialog
      className="@RtspCameraCreateDialog"
      isOpen={isOpen}
      onOpenChange={(open) => {
        if (!open) handleClose();
      }}
      title={t.hardwareView.rtspCameraCreateDialog.title}
      content={
        <Col gap3>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.rtspCameraCreateDialog.fields.name}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded border border-neutral/20 px-3 py-1.5 text-sm outline-none focus:border-primary"
              disabled={isSubmitting}
            />
          </Col>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.rtspCameraCreateDialog.fields.streamUrl}
            </label>
            <input
              type="text"
              value={streamUrl}
              onChange={(e) => setStreamUrl(e.target.value)}
              placeholder={
                t.hardwareView.rtspCameraCreateDialog.fields
                  .streamUrlPlaceholder
              }
              className="rounded border border-neutral/20 px-3 py-1.5 text-sm outline-none focus:border-primary"
              disabled={isSubmitting}
            />
          </Col>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.rtspCameraCreateDialog.fields.snapshotUrl}
            </label>
            <input
              type="text"
              value={snapshotUrl}
              onChange={(e) => setSnapshotUrl(e.target.value)}
              placeholder={
                t.hardwareView.rtspCameraCreateDialog.fields
                  .snapshotUrlPlaceholder
              }
              className="rounded border border-neutral/20 px-3 py-1.5 text-sm outline-none focus:border-primary"
              disabled={isSubmitting}
            />
          </Col>
          <Col gap1>
            <label className="text-sm text-neutral/60">
              {t.hardwareView.rtspCameraCreateDialog.fields.sidecar}
            </label>
            <Select
              options={sidecarOptions}
              value={sidecarId}
              onChange={setSidecarId}
              placeholder={
                t.hardwareView.rtspCameraCreateDialog.fields.sidecarPlaceholder
              }
            />
          </Col>
        </Col>
      }
      footer={
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            color="neutral"
            onPress={handleClose}
            isDisabled={isSubmitting}
          >
            {t.hardwareView.rtspCameraCreateDialog.actions.cancel}
          </Button>
          <Button
            icon={<LucideCheck />}
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit || isSubmitting}
          >
            {isSubmitting
              ? t.hardwareView.rtspCameraCreateDialog.actions.creating
              : t.hardwareView.rtspCameraCreateDialog.actions.create}
          </Button>
        </div>
      }
    />
  );
};
